import { apiJson } from '../api';
import { getAccessToken } from '../auth';
import type { Channel, StoryElement } from '../types';
import type { SavedSearchRunStart } from './savedSearchRepository';
import { TLDR_CHANNEL_ID } from '../utils/channelUrl';

export async function listChannels(): Promise<Channel[]> {
  const data = await apiJson<Channel[]>('/api/channels');
  return Array.isArray(data) ? data : [];
}

/**
 * How deep the reader's feed goes on load, and how deep "More stories" takes
 * it. There is no paging cursor behind this: the second fetch asks for the
 * full list rather than a next page, which is why the control appears once and
 * then stops. Almost nobody reads past the first depth, so the deeper list is
 * a request rather than the default.
 */
export const READER_FEED_DEPTH = 50;
export const READER_FEED_FULL_DEPTH = 200;

/**
 * The TLDR channel is readable without a session (issue #825's logged-out
 * feed). Sending the request unauthenticated when there is no token keeps the
 * anonymous read off the refresh/session-expired path, which would otherwise
 * bounce a logged-out visitor to /login instead of serving the public feed.
 *
 * `defer_details` asks the server to leave out the topic alternates and the
 * score-breakdown blob, which this client hydrates per row on expand and per
 * story on modal open. Together they were 72% of the response.
 *
 * `user_state=false` drops the per-user read/flag decoration, a database round
 * trip on the feed's critical path for flags this reader does not draw. It is
 * off by default so a signed-in reader's first paint costs the server exactly
 * what an anonymous one does; `admin` turns both it and the admin score
 * overlay back on for the second request an admin's view makes.
 *
 * The TLDR feed takes /api/public/tldr/feed instead, which is that same
 * response -- deferred details, no user state -- served from the CDN edge
 * rather than the origin. It is a separate URL rather than a header on this
 * one because the win is a *shared* cache entry: every reader asking for the
 * same bytes has to ask for them by the same name, and the endpoint that
 * serves them has to be one that cannot know who is asking. Sent with
 * `public: true` unconditionally for the same reason -- attaching a token
 * would make the request vary by viewer at the edge for a response that does
 * not. An admin's second request keeps the channel endpoint, which is where
 * the per-viewer layers live and where nothing is cached.
 */
export async function listElements(
  channelId: string,
  limit = READER_FEED_DEPTH,
  { admin = false }: { admin?: boolean } = {},
): Promise<StoryElement[]> {
  if (!admin && channelId === TLDR_CHANNEL_ID) {
    const data = await apiJson<StoryElement[]>(
      `/api/public/tldr/feed?limit=${limit}`,
      { public: true }
    );
    return Array.isArray(data) ? data : [];
  }
  const layers = admin ? 'admin=true&user_state=true' : 'user_state=false';
  const data = await apiJson<StoryElement[]>(
    `/api/channels/${encodeURIComponent(channelId)}/elements?limit=${limit}&defer_details=true&${layers}`,
    { public: !getAccessToken() }
  );
  return Array.isArray(data) ? data : [];
}

export async function runTldrChannel(): Promise<SavedSearchRunStart> {
  return apiJson<SavedSearchRunStart>('/api/channels/tldr/run', {
    method: 'POST',
  });
}
